import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  LocationOnOutlined,
  Refresh as RefreshIcon,
  OpenInNew
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { User } from '../../../types';
import { ZERDE_PRIMARY } from '../types';

// Фирменная иконка маркера для карты
const customMarkerIcon = L.divIcon({
  className: 'custom-leaflet-marker',
  html: `
    <div style="
      width: 28px;
      height: 28px;
      background: #1570ef;
      border: 3px solid #ffffff;
      border-radius: 50%;
      box-shadow: 0 4px 12px rgba(21, 112, 239, 0.4);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="width: 8px; height: 8px; background: #ffffff; border-radius: 50%;"></div>
    </div>
  `,
  iconSize: [28, 28],
  iconAnchor: [14, 14]
});

interface UserGeoSectionProps {
  user: User;
  loadingGeo: boolean;
  onRefreshGeo: () => void;
  currentAddress: string;
  hasValidCoords: boolean;
  currentLatStr: string | null;
  currentLngStr: string | null;
  currentAccuracy: number | null;
  currentGeoUpdatedAt: string | null;
  lat: number | null;
  lng: number | null;
}

export const UserGeoSection: React.FC<UserGeoSectionProps> = ({
  user,
  loadingGeo,
  onRefreshGeo,
  currentAddress,
  hasValidCoords,
  currentLatStr,
  currentLngStr,
  currentAccuracy,
  currentGeoUpdatedAt,
  lat,
  lng
}) => {
  return (
    <Box id="user-section-geo" sx={{ scrollMarginTop: '60px' }}>
      <Paper elevation={0} sx={{ p: 3.5, bgcolor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnOutlined sx={{ color: ZERDE_PRIMARY, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
              Фактический адрес и координаты аптеки
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={loadingGeo ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={onRefreshGeo}
            disabled={loadingGeo}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '0.8rem',
              color: '#475569',
              borderColor: '#cbd5e1'
            }}
          >
            {loadingGeo ? 'Обновление...' : 'Обновить'}
          </Button>
        </Box>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
          Данные фиксируются через GPS смартфона или браузер и передаются карты
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <Paper elevation={0} sx={{ p: 2.2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>АДРЕС АПТЕКИ</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a', mt: 0.5 }}>
              {currentAddress || (loadingGeo ? 'Загрузка адреса...' : 'Адрес ещё не определен')}
            </Typography>
          </Paper>

          <Paper elevation={0} sx={{ p: 2.2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>GPS КООРДИНАТЫ И ТОЧНОСТЬ</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a', mt: 0.5, fontFamily: 'monospace' }}>
              {hasValidCoords ? `${currentLatStr}, ${currentLngStr}` : (loadingGeo ? 'Загрузка координат...' : 'Координаты не переданы')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
              {currentAccuracy != null && (
                <Typography variant="caption" sx={{ color: currentAccuracy <= 30 ? '#10b981' : '#f97316', fontWeight: 600 }}>
                  Точность: ±{Math.round(currentAccuracy)} м
                </Typography>
              )}
              {currentGeoUpdatedAt && (
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Фиксация: {new Date(currentGeoUpdatedAt).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>

        {hasValidCoords ? (
          <Box sx={{ height: 320, borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <MapContainer
              key={`${lat}-${lng}`}
              center={[lat!, lng!]}
              zoom={16}
              scrollWheelZoom={false}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[lat!, lng!]} icon={customMarkerIcon} />
            </MapContainer>
          </Box>
        ) : loadingGeo ? (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
            <CircularProgress size={28} sx={{ color: ZERDE_PRIMARY, mb: 1 }} />
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Запрос координат из базы геопозиций...
            </Typography>
          </Box>
        ) : (
          <Alert severity="info" sx={{ borderRadius: '10px' }}>
            Координаты геолокации пока не зафиксированы.{' '}
            {user.role !== 'user' && user.role !== 'storage' ? (
              'Для администраторов и офисных сотрудников геолокация аптеки не требуется.'
            ) : (
              'Координаты фиксируются сотрудником аптеки через мобильный телефон или браузер при входе.'
            )}
          </Alert>
        )}

        {hasValidCoords && (
          <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={() => window.open(`https://2gis.kz/geo/${lng},${lat}`, '_blank')}
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              2GIS
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={() => window.open(`https://yandex.kz/maps/?ll=${lng},${lat}&z=16&pt=${lng},${lat}`, '_blank')}
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              Яндекс Карты
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={() => window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank')}
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              Google Карты
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default UserGeoSection;
